import { FunctionComponent, useContext } from 'react';

import {
  Input,
  Group,
  FieldError,
  ToggleableField,
  ToggleableFieldProps,
  FormHeader,
  FormDivider,
  Select,
  ResponsiveMargin,
  Field,
  Checkbox,
  ResponsivePadding,
} from '../../components';
import { PaymentsFieldsProps } from './PaymentsFields.types';
import { CardProcessingInfoFields } from './CardProcessingInfoFields';
import { useWindowSize } from '../../hooks';
import {
  refundPolicies,
  paymentProductTypes,
  reasonsForApplying,
  booleanOptions,
} from '../../util/registration.util';
import { isPayfacMerchant } from '../../util/merchant.util';
import { BrandStore, BrandMCC, AuthStore } from '../../context';
import { FieldPermissionFlag } from '../../components/FieldPermissionFlag';
import Autocomplete from '../../components/Autocomplete/Autocomplete';
import { OptionItem } from '../../components/Autocomplete';

export const PaymentsFields: FunctionComponent<PaymentsFieldsProps> = props => {
  const { isMobile } = useWindowSize();
  const {
    state: { brand, mcc },
  } = useContext(BrandStore);
  const {
    state: { auth },
  } = useContext(AuthStore);

  const {
    values: formValues,
    form: { mutators, change },
  } = props;

  const defaultFieldProps: Partial<ToggleableFieldProps<any, HTMLElement>> = {
    toggleable: props.mode === 'review',
    defaultMode: props.mode === 'review' ? 'display' : 'edit',
  };

  return (
    <>
      {props.mode === 'review' && (
        <FormHeader tag="h3" data-testid="review-payments" bordered>
          Payments
        </FormHeader>
      )}
      <ResponsivePadding>
        <Group direction="column" space="1rem/0px">
          <Group direction="column">
            <ToggleableField
              name="reason_for_applying"
              label={
                isMobile
                  ? 'Reason for applying'
                  : 'Why are you applying for merchant processing?'
              }
              {...defaultFieldProps}
              type="select"
              options={reasonsForApplying}
              optionLabelKey="description"
              optionValueKey="key"
            >
              {props => (
                <Select {...props.input}>
                  <option value="" disabled />
                  {reasonsForApplying.map((e, index) => (
                    <option key={index} value={e.key}>
                      {e.description}
                    </option>
                  ))}
                </Select>
              )}
            </ToggleableField>
            <FieldError name="reason_for_applying" />
          </Group>

          {!isPayfacMerchant(auth?.merchant) && (
            <>
              <Group direction="column">
                <ToggleableField
                  name="account_data_compromised"
                  label={
                    'Have you ever experienced an Account Data Compromise?'
                  }
                  {...defaultFieldProps}
                  type="select"
                  options={booleanOptions}
                  optionLabelKey="description"
                  optionValueKey="key"
                >
                  {props => (
                    <Select
                      {...props.input}
                      tooltip="An Account Data Compromise (ADC) is an intrusion into computer system(s) or access to physical cardholder data where unauthorized disclosure, modification or destruction of cardholder data is suspected."
                    >
                      <option value="" disabled />
                      {booleanOptions.map((e, index) => (
                        <option key={index} value={e.key}>
                          {e.description}
                        </option>
                      ))}
                    </Select>
                  )}
                </ToggleableField>
                <FieldError name="account_data_compromised" />
              </Group>
              {formValues.account_data_compromised === '1' && (
                <Group direction="column">
                  <ToggleableField
                    name="account_data_compromised_remediated"
                    label={'Have you completed remediation?'}
                    {...defaultFieldProps}
                    type="select"
                    options={booleanOptions}
                    optionLabelKey="description"
                    optionValueKey="key"
                  >
                    {props => (
                      <Select {...props.input}>
                        <option value="" disabled />
                        {booleanOptions.map((e, index) => (
                          <option key={index} value={e.key}>
                            {e.description}
                          </option>
                        ))}
                      </Select>
                    )}
                  </ToggleableField>
                  <FieldError name="account_data_compromised_remediated" />
                </Group>
              )}
            </>
          )}
          <Group direction="column">
            <ToggleableField
              name="refund_policy"
              label={isMobile ? 'Refund Policy' : 'What is your refund policy?'}
              {...defaultFieldProps}
              type="select"
              options={refundPolicies}
              optionLabelKey="description"
              optionValueKey="key"
            >
              {props => (
                <Select {...props.input}>
                  <option value="" disabled />
                  {refundPolicies.map((e, index) => (
                    <option key={index} value={e.key!}>
                      {e.description}
                    </option>
                  ))}
                </Select>
              )}
            </ToggleableField>
            <FieldError name="refund_policy" />
          </Group>

          {brand?.showProductDropdown && (
            // TODO: make more than in-person and mobile-reader selectable
            <Group direction="column">
              <ToggleableField
                name="chosen_processing_method"
                label={
                  isMobile
                    ? 'How do you accept payments?'
                    : 'How do you primarily accept payments?'
                }
                {...defaultFieldProps}
                type="select"
                options={paymentProductTypes}
                optionLabelKey="description"
                optionValueKey="key"
              >
                {props => (
                  <Select {...props.input}>
                    <option value="" disabled />
                    {paymentProductTypes.map((e, index) => (
                      <option key={index} value={e.key}>
                        {e.description}
                      </option>
                    ))}
                  </Select>
                )}
              </ToggleableField>
              <FieldError name="chosen_processing_method" />
            </Group>
          )}

          <Group direction="column">
            <ToggleableField
              name="service_you_provide"
              label={
                isMobile
                  ? 'Service/product you provide'
                  : 'What do you sell or what service do you provide?'
              }
              {...defaultFieldProps}
            >
              {props => (
                <Autocomplete
                  inputProps={props.input}
                  formatter={value =>
                    value?.includes(' - ') ? value?.split(' - ')[1] : value
                  }
                  onChange={props.input.onChange}
                  onSelect={value => {
                    props.input.onChange(value.label);
                    change('mcc', value.name);
                  }}
                  options={(mcc || []).reduce((acc, curr) => {
                    if (curr.name && curr.displayName) {
                      const value = String(
                        curr.displayName?.split(`${curr.name} -`)[1],
                      ).trim();
                      if (value && !value.toLowerCase().includes('n/a')) {
                        acc.push({
                          ...curr,
                          label: value,
                          key: curr.displayName,
                        });
                      }
                    }
                    return acc;
                  }, [] as (BrandMCC & OptionItem)[])}
                />
              )}
            </ToggleableField>
            <FieldError name="service_you_provide" />
          </Group>

          {formValues.chosen_processing_method === 'shopping-cart' ? (
            <>
              <FormDivider />
              <FormHeader tag="h1">Shopping Cart</FormHeader>
              <CardProcessingInfoFields {...props} isShoppingCart />
              <FormDivider />
              <FormHeader tag="h1">Keyed-in Transactions</FormHeader>
              <CardProcessingInfoFields {...props} />
              <FormDivider />
            </>
          ) : (
            <CardProcessingInfoFields {...props} />
          )}

          <FieldPermissionFlag
            fields={[
              'annual_gross_ach_revenue',
              'avg_ach_transaction',
              'largest_ach_transaction',
            ]}
          >
            {({ atLeastOneRead, allWrite }) =>
              atLeastOneRead ? (
                // only render this section if at least one of the fields is readable
                <Group direction={isMobile ? 'column-reverse' : 'row'}>
                  <Group
                    direction="column"
                    space="1rem/0px"
                    style={{ flex: '50%' }}
                  >
                    {/* Below */}

                    {allWrite ? (
                      // only render this checkbox if all of these fields are writeable
                      <Group direction="row">
                        <ResponsiveMargin mobile="1rem 0.5rem" desktop="0">
                          <Field
                            name="shouldAutoCalculateACHValues"
                            type="checkbox"
                          >
                            {props => (
                              <Checkbox
                                {...props.input}
                                id="shouldAutoCalculateACHValues"
                                data-testid="shouldAutoCalculateACHValues"
                                label="Fill ACH fields with recommended amounts based on my credit card processing volume"
                                onChange={e => {
                                  props.input.onChange(e);

                                  if (e.target.checked) {
                                    mutators.setCalculatedAchFields();
                                  } else {
                                    mutators.clearCalculatedAchFields();
                                  }
                                }}
                              />
                            )}
                          </Field>
                        </ResponsiveMargin>
                      </Group>
                    ) : null}

                    <Group direction="column">
                      <ToggleableField
                        currency
                        name="annual_gross_ach_revenue"
                        label="Annual Gross ACH Revenue $"
                        format={v => (v ? v.replace(/[^0-9.,$%]/g, '') : '')}
                        {...defaultFieldProps}
                      >
                        {props => (
                          <Input
                            {...props.input}
                            currency
                            tooltip={
                              <>
                                <p>
                                  What is the annual gross revenue you expect
                                  your business to run in e-checks?
                                </p>
                                <p>
                                  If you’re not sure, we recommend that you use
                                  40% of your expected annual credit card
                                  revenue.
                                </p>
                                <p>
                                  Adding your expected annual ACH/e-check
                                  revenue now will expedite the setup process
                                  for ACH processing.
                                </p>
                              </>
                            }
                            onChange={e => {
                              props.input.onChange(e);
                              // if the ach field values are different from calculated ach field values, automatically flip the 'shouldAutoCalculateACHValues' checkbox to 'false'
                              mutators.setShouldAutoCalculateACHValues();
                            }}
                          />
                        )}
                      </ToggleableField>
                      <FieldError name="annual_gross_ach_revenue" />
                    </Group>
                    <Group direction="column">
                      <ToggleableField
                        currency
                        name="avg_ach_transaction"
                        label="Average ACH Transaction Size $"
                        format={v => (v ? v.replace(/[^0-9.,$%]/g, '') : '')}
                        {...defaultFieldProps}
                      >
                        {props => (
                          <Input
                            {...props.input}
                            currency
                            tooltip={
                              <>
                                <p>
                                  What is the average transaction size you
                                  expect your business to run in e-checks?
                                </p>
                                <p>
                                  If you’re not sure, we recommend that you use
                                  your average credit card transaction size.
                                </p>
                                <p>
                                  Adding your expected ACH/e-check average
                                  transaction size now will expedite the setup
                                  process for ACH processing.
                                </p>
                              </>
                            }
                            onChange={e => {
                              props.input.onChange(e);
                              // if the ach field values are different from calculated ach field values, automatically flip the 'shouldAutoCalculateACHValues' checkbox to 'false'
                              mutators.setShouldAutoCalculateACHValues();
                            }}
                          />
                        )}
                      </ToggleableField>
                      <FieldError name="avg_ach_transaction" />
                    </Group>

                    <Group direction="column">
                      <ToggleableField
                        currency
                        name="largest_ach_transaction"
                        label="Largest ACH Transaction Size $"
                        format={v => (v ? v.replace(/[^0-9.,$%]/g, '') : '')}
                        {...defaultFieldProps}
                      >
                        {props => (
                          <Input
                            {...props.input}
                            currency
                            tooltip={
                              <>
                                <p>
                                  What is the largest transaction size you
                                  expect your business to run in e-checks?
                                </p>
                                <p>
                                  If you’re not sure, we recommend that you use
                                  your largest credit card transaction size.
                                </p>
                                <p>
                                  Adding your expected ACH/e-check largest
                                  transaction size now will expedite the setup
                                  process for ACH processing.
                                </p>
                              </>
                            }
                            onChange={e => {
                              props.input.onChange(e);
                              // if the ach field values are different from calculated ach field values, automatically flip the 'shouldAutoCalculateACHValues' checkbox to 'false'
                              mutators.setShouldAutoCalculateACHValues();
                            }}
                          />
                        )}
                      </ToggleableField>
                      <FieldError name="largest_ach_transaction" />
                    </Group>

                    {/* Above */}
                  </Group>
                </Group>
              ) : (
                (null as any)
              )
            }
          </FieldPermissionFlag>
        </Group>
      </ResponsivePadding>
      {props.children}
    </>
  );
};
